import React, { useState, useEffect, useRef } from 'react';
import { quotesService } from '../../services/quotesService';
import './ConversationalQuoteBuilder.css';

const ConversationalQuoteBuilder = ({ onClose, onQuoteCreated, onFinalizeSuccess }) => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentActions, setCurrentActions] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchingClients, setSearchingClients] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    startConversation();
    searchClients('');
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (showClientSelector) {
        searchClients(clientSearch);
      }
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [clientSearch, showClientSelector]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const searchClients = async (query) => {
    try {
      setSearchingClients(true);
      const response = await quotesService.searchClientsForConversation(query);
      if (response.success) {
        setClients(response.clients || []);
      }
    } catch (error) {
      console.error('Error searching clients:', error);
    } finally {
      setSearchingClients(false);
    }
  };

  const startConversation = async () => {
    try {
      setIsLoading(true);
      const response = await quotesService.startConversation();
      
      if (response.success) {
        setSessionId(response.sessionId);
        setMessages([response.message]);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      setMessages([{
        role: 'ai',
        message: 'Sorry, I encountered an error starting our conversation. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!selectedClient || !sessionId) {
      alert('Please select a client first');
      return;
    }

    try {
      setFinalizing(true);
      const response = await quotesService.finalizeConversation(sessionId, selectedClient.id);
      
      if (response && response.success) {
        if (response.isMultiOption && response.quotes) {
          // Handle multi-option quotes - pass the whole group so the
          // Quote Builder can render a tabbed comparison view
          console.log('Created multi-option quotes:', response.quotes);
          
          if (onFinalizeSuccess) {
            onFinalizeSuccess(response.quotes);
          }
          
          // Maintain backward compatibility with old callback (uses the first option's id)
          if (onQuoteCreated) {
            onQuoteCreated(response.quotes[0].id);
          }
          
          onClose();
        } else if (response.quote) {
          // Handle single quote (existing logic)
          if (onFinalizeSuccess) {
            onFinalizeSuccess(response.quote);
          }
          
          // Maintain backward compatibility with old callback
          if (onQuoteCreated) {
            onQuoteCreated(response.quote.id);
          }
          
          onClose();
        }
      }
    } catch (error) {
      console.error('Error finalizing quote:', error);
      alert('Failed to create quote: ' + (error.response?.data?.error || error.message));
    } finally {
      setFinalizing(false);
    }
  };

  const sendMessage = async (messageText = null) => {
    const text = messageText || inputMessage.trim();
    if (!text || !sessionId) return;

    const userMessage = {
      role: 'agent',
      message: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await quotesService.sendConversationMessage(sessionId, text);
      
      if (response.success) {
        const aiMessage = {
          role: 'ai',
          message: response.message,
          timestamp: new Date(),
          actions: response.actions
        };
        
        setMessages(prev => [...prev, aiMessage]);
        setCurrentActions(response.actions);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'ai',
        message: 'Sorry, I encountered an error. Could you please try that again?',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !sessionId) return;

    setUploadingFile(true);
    
    const userMessage = {
      role: 'agent',
      message: `📎 Uploading ${file.name}...`,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await quotesService.uploadConversationFile(sessionId, file);
      
      if (response.success) {
        const aiMessage = {
          role: 'ai',
          message: response.message,
          timestamp: new Date(),
          actions: response.actions
        };
        
        setMessages(prev => [...prev, aiMessage]);
        setCurrentActions(response.actions);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMessage = {
        role: 'ai',
        message: `Sorry, I couldn't process that file. ${error.response?.data?.error || 'Please try again.'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleActionClick = (action) => {
    if (action.value === 'upload') {
      fileInputRef.current?.click();
    } else {
      sendMessage(action.value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className="conversational-quote-builder-overlay">
      <div className="conversational-quote-builder">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-content">
            <h2>🤖 AI Quote Builder</h2>
            <p className="chat-subtitle">Let's create an amazing quote together</p>
          </div>
          <button className="chat-close-button" onClick={onClose}>×</button>
        </div>

        {/* Messages Container */}
        <div className="chat-messages">
          {messages.map((msg, index) => (
            <div key={index} className={`chat-message ${msg.role}`}>
              <div className="message-bubble">
                <div className="message-content">
                  {msg.message.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
                <div className="message-timestamp">
                  {formatTimestamp(msg.timestamp)}
                </div>
              </div>

              {/* Action Buttons */}
              {msg.actions && msg.actions.type === 'choose_method' && (
                <div className="message-actions">
                  {msg.actions.options.map((option) => (
                    <button
                      key={option.id}
                      className="action-button primary"
                      onClick={() => handleActionClick(option)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}

              {/* File Upload Zone */}
              {msg.actions && msg.actions.type === 'file_upload' && (
                <div className="message-file-upload">
                  <div className="file-upload-zone" onClick={() => fileInputRef.current?.click()}>
                    <div className="upload-icon">📁</div>
                    <p>Drop your PDF or screenshot here</p>
                    <button className="upload-button">Choose File</button>
                  </div>
                </div>
              )}

              {/* Confirmation Buttons */}
              {msg.actions && msg.actions.type === 'confirm' && (
                <div className="message-actions">
                  {msg.actions.options.map((option) => (
                    <button
                      key={option.id}
                      className={`action-button ${option.id === 'confirm' ? 'success' : 'secondary'}`}
                      onClick={() => handleActionClick(option)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Processing Indicator */}
              {msg.actions && msg.actions.type === 'processing_file' && (
                <div className="processing-indicator">
                  <div className="spinner"></div>
                  <p>Analyzing your file...</p>
                </div>
              )}
            </div>
          ))}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="chat-message ai">
              <div className="message-bubble typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Client Selection & Finalize Bar */}
        <div className="chat-client-bar">
          <div className="client-selector">
            <label>Select Client:</label>
            {!showClientSelector ? (
              <button 
                className="select-client-button"
                onClick={() => setShowClientSelector(true)}
              >
                {selectedClient ? selectedClient.name : 'Choose Client'}
              </button>
            ) : (
              <div className="client-search-dropdown">
                <input
                  type="text"
                  className="client-search-input"
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  autoFocus
                />
                <div className="client-list">
                  {searchingClients ? (
                    <div className="client-item loading">Searching...</div>
                  ) : clients.length > 0 ? (
                    clients.map(client => (
                      <div
                        key={client.id}
                        className="client-item"
                        onClick={() => {
                          setSelectedClient(client);
                          setShowClientSelector(false);
                          setClientSearch('');
                        }}
                      >
                        <div className="client-name">{client.name}</div>
                        <div className="client-details">{client.email}</div>
                      </div>
                    ))
                  ) : (
                    <div className="client-item empty">No clients found</div>
                  )}
                </div>
                <button 
                  className="close-dropdown"
                  onClick={() => setShowClientSelector(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          <button
            className="finalize-button"
            onClick={handleFinalize}
            disabled={!selectedClient || finalizing || !messages.some(m => m.actions?.type === 'confirm')}
            title={!selectedClient ? 'Select a client first' : 'Create quote in CRM'}
          >
            {finalizing ? 'Creating Quote...' : '✓ Finalize Quote'}
          </button>
        </div>

        {/* Input Area */}
        <div className="chat-input-area">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf,image/png,image/jpeg,image/jpg,image/gif,image/bmp,image/heic,image/heif"
            style={{ display: 'none' }}
          />
          
          <div className="chat-input-container">
            <button 
              className="attach-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile || isLoading}
              title="Upload file"
            >
              📎
            </button>
            
            <textarea
              className="chat-input"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || uploadingFile}
              rows="1"
            />
            
            <button 
              className="send-button"
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading || uploadingFile}
            >
              {isLoading ? '...' : '→'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationalQuoteBuilder;
